<script setup lang="ts">
import {
  AddMedicalSystemOptionsDto,
  MedicalSystemBeamLimitingDeviceType,
  MedicalSystemSubCategory,
  RtSystemCategory,
  RtSystemManufacturer,
  RtSystemModel,
  type GetMedicalSystemTemplateRequest as SelectedTemplateOptions,
} from '@/api/generator';
import { api } from '@/api';
import { ref, computed, watchEffect, watch } from 'vue';
import Dialog from '@/components/Dialog/Dialog.vue';
import Autocomplete from '@/components/forms/Autocomplete/Autocomplete.vue';
import type { SelectItemTypeObject } from '@/components/forms/types';
import { getTranslatedOptions } from '@/utils/helper';
import { mdiRefresh } from '@mdi/js';
import { fromPairs } from 'lodash';
import {
  medicalSystemTypeSubCategory,
  medicalSystemTypeManufacturer,
  medicalSystemTypeModel,
} from '@/components/icons/icons.ts';

const modelValue = defineModel<boolean>({ type: Boolean, default: false });
const emits = defineEmits(['submit']);

type Section = keyof SelectedTemplateOptions;

type AddMedicalSystemSelectOptions = Record<Section, SelectItemTypeObject<string>[]>;

const selections = ref<SelectedTemplateOptions>({});

const isLoadingOptions = ref(false);

const loadedOptions = ref<AddMedicalSystemOptionsDto>();

watch(modelValue, (isOpen) => {
  if (!isOpen && Object.keys(selections.value).length) {
    selections.value = {};
    loadedOptions.value = undefined;
    showAllManufacturers.value = false;
  }
});

const sectionOrder: Section[] = [
  'category',
  'subCategory',
  'manufacturer',
  'model',
  'subModel',
  'beamLimitingDeviceType',
  'beamProduction',
];

const allManufacturers = ref<string[]>();
const showAllManufacturers = ref(false);

const canSubmit = computed(() => {
  const countSelections = Object.values(selections.value).filter(Boolean).length;
  const countOptions = Object.values(options.value).filter((option) => option.length).length;
  // Check if a selection has been made for each option
  return countSelections === countOptions && !isLoadingOptions.value;
});

const submit = () => {
  emits('submit', selections.value);
};

async function getAllManufacturers() {
  if (!allManufacturers.value) {
    const { result } = await api.medicalSystemsApi.getAllManufacturers();
    if (result) {
      allManufacturers.value = result;
    }
  }
  showAllManufacturers.value = true;
}

function updateSelection(targetSection: Section) {
  // reset `showAllManufacturers` when a selection before `manufacturer` has been made
  if (
    sectionOrder.findIndex((section) => section === targetSection) <
    sectionOrder.findIndex((section) => section === 'manufacturer')
  ) {
    showAllManufacturers.value = false;
  }

  // Remove all preceding selections
  let selectionNeedsUpdate = false;
  const newSelections = sectionOrder.reduce((prevSelection, section) => {
    if (selectionNeedsUpdate) {
      return { ...prevSelection, [section]: undefined };
    } else {
      if (section === targetSection) {
        selectionNeedsUpdate = true;
      }
      return prevSelection;
    }
  }, selections.value);
  selections.value = newSelections;
}

async function updateOptions(selected: SelectedTemplateOptions = {}) {
  isLoadingOptions.value = true;
  const { result } = await api.medicalSystemsApi.getNewRTSystemOptions(selected);
  if (result) {
    loadedOptions.value = result;

    // when receiving options, that have only one possible option, this options should be
    // set immediately if no value has been set before
    let needsReload = false;
    const newSelections = fromPairs(
      Object.entries(selections.value).map(([key, value]) => {
        const targetResult = result[key as Section];
        if (!value && targetResult?.length === 1) {
          needsReload = true;
          return [key, targetResult[0]];
        } else {
          return [key, value];
        }
      }),
    );
    if (needsReload) {
      selections.value = newSelections;
    }
  }
  isLoadingOptions.value = false;
}

function getTransformedOptions(
  keys: string[] | undefined | null,
  context: string,
  otherEnums: string[],
) {
  if (!keys) {
    return [];
  }

  // translate and sort options
  const translatedOptions: SelectItemTypeObject<string>[] = getTranslatedOptions(
    keys,
    context,
  ).sort((a, b) => a.title.localeCompare(b.title));

  // other-Enums should be moved to the end with a divider
  let filteredOtherOption: SelectItemTypeObject<string> | undefined;
  const transformedOptions: SelectItemTypeObject<string>[] = translatedOptions.filter(
    (translatedOption) => {
      if (otherEnums.includes(translatedOption.value)) {
        filteredOtherOption = translatedOption;
        return false;
      } else {
        return true;
      }
    },
  );
  if (filteredOtherOption) {
    transformedOptions.push({
      ...filteredOtherOption,
      props: { class: 'MedicalSystemAddDialog_divider' },
    });
  }
  return transformedOptions;
}

const options = computed<AddMedicalSystemSelectOptions>(() => {
  return {
    category: getTransformedOptions(loadedOptions.value?.category, 'RtSystemCategoryEnum', [
      RtSystemCategory.OtherMedicalSystem,
    ]),
    subCategory: getTransformedOptions(
      loadedOptions.value?.subCategory,
      'MedicalSystemSubCategoryEnum',
      [
        MedicalSystemSubCategory.OtherImagingMachine,
        MedicalSystemSubCategory.OtherMedicalSystem,
        MedicalSystemSubCategory.OtherSoftware,
        MedicalSystemSubCategory.OtherTreatmentMachine,
      ],
    ),
    manufacturer: getTransformedOptions(
      (showAllManufacturers.value && allManufacturers.value) || loadedOptions.value?.manufacturer,
      'RtSystemManufacturerEnum',
      [RtSystemManufacturer.MsOther],
    ),
    model: getTransformedOptions(loadedOptions.value?.model, 'RtSystemModelEnum', [
      RtSystemModel.OtherModel,
    ]),
    subModel: getTransformedOptions(loadedOptions.value?.subModel, 'MedicalSystemSubModelEnum', []),
    beamLimitingDeviceType: getTransformedOptions(
      loadedOptions.value?.beamLimitingDeviceType,
      'MedicalSystemBeamLimitingDeviceTypeEnum',
      [MedicalSystemBeamLimitingDeviceType.OtherBeamlimitingDeviceType],
    ),
    beamProduction: getTransformedOptions(
      loadedOptions.value?.beamProduction,
      'MedicalSystemBeamProductionEnum',
      [],
    ),
  };
});

watchEffect(() => updateOptions(selections.value));
</script>

<template>
  <Dialog v-model="modelValue" size="medium">
    <template #title> {{ $t('ChooseModel') }} </template>

    <template #text>
      <Autocomplete
        v-model="selections.category"
        :label="$t('Category_one')"
        :items="options.category"
        :disabled="isLoadingOptions || options.category.length === 1"
        :placeholder="$t('Select')"
        @update:modelValue="updateSelection('category')"
      />
      <Autocomplete
        v-if="options.subCategory.length"
        v-model="selections.subCategory"
        :prependInnerIcon="medicalSystemTypeSubCategory"
        class="gutterTop"
        :label="$t('Type_one')"
        :items="options.subCategory"
        :disabled="isLoadingOptions || options.subCategory.length === 1"
        :placeholder="$t('Select')"
        @update:modelValue="updateSelection('subCategory')"
      />
      <Autocomplete
        v-if="options.manufacturer.length"
        v-model="selections.manufacturer"
        :prependInnerIcon="medicalSystemTypeManufacturer"
        class="gutterTop"
        :label="$t('Manufacturer')"
        :items="options.manufacturer"
        :disabled="isLoadingOptions || options.manufacturer.length === 1"
        :active="true"
        :placeholder="$t('Select')"
        @update:modelValue="updateSelection('manufacturer')"
      >
        <template v-if="!showAllManufacturers" #appendItem>
          <v-divider class="mb-2"></v-divider>
          <v-list-item :title="$t('ShowAllManufacturers')" @click="getAllManufacturers">
            <template #append>
              <v-icon :icon="mdiRefresh" />
            </template>
          </v-list-item>
        </template>
      </Autocomplete>
      <Autocomplete
        v-if="options.model.length"
        v-model="selections.model"
        :prependInnerIcon="medicalSystemTypeModel"
        class="gutterTop"
        :label="$t('Model')"
        :items="options.model"
        :disabled="isLoadingOptions || options.model.length === 1"
        :placeholder="$t('Select')"
        @update:modelValue="updateSelection('model')"
      />
      <Autocomplete
        v-if="options.subModel.length"
        v-model="selections.subModel"
        class="gutterTop"
        :label="$t('SubModel')"
        :items="options.subModel || []"
        :disabled="isLoadingOptions || options.subModel.length === 1"
        :placeholder="$t('Select')"
        @update:modelValue="updateSelection('subModel')"
      />
      <Autocomplete
        v-if="options.beamLimitingDeviceType.length"
        v-model="selections.beamLimitingDeviceType"
        class="gutterTop"
        :label="$t('BeamLimitingDeviceType')"
        :items="options.beamLimitingDeviceType || []"
        :disabled="isLoadingOptions || options.beamLimitingDeviceType.length === 1"
        :placeholder="$t('Select')"
        @update:modelValue="updateSelection('beamLimitingDeviceType')"
      />
      <Autocomplete
        v-if="options.beamProduction.length"
        v-model="selections.beamProduction"
        class="gutterTop"
        :label="$t('BeamProduction')"
        :items="options.beamProduction || []"
        :disabled="isLoadingOptions || options.beamProduction.length === 1"
        :placeholder="$t('Select')"
        @update:modelValue="updateSelection('beamProduction')"
      />
    </template>

    <template #actions>
      <SecondaryVBtn :disabled="!canSubmit" @click="submit">
        {{ $t('Apply') }}
      </SecondaryVBtn>
    </template>
  </Dialog>
</template>

<style lang="scss" scoped>
:global(.MedicalSystemAddDialog_divider) {
  border-top: 1px solid rgb(var(--v-theme-primary-lighten-4));
}
</style>
